import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ServiceCategoryCard } from "./ServiceCategoryCard";
import { findTopLevelItems } from "~/lib/data/hierarchy";
import type {
  ItemAction,
  MerchantCategoryRelationship,
  ServiceItem,
} from "~/lib/types";

type Props = {
  items: ServiceItem[];
  relationships: MerchantCategoryRelationship[];
  actions: ItemAction[];
};

export const ServiceCategoryGrid = ({
  items,
  relationships,
  actions,
}: Props) => {
  const { t } = useTranslation();

  const topLevelItems = useMemo(
    () => findTopLevelItems(items, relationships),
    [items, relationships],
  );

  const childCountByParent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of relationships) {
      counts.set(r.related_item_id, (counts.get(r.related_item_id) ?? 0) + 1);
    }
    return counts;
  }, [relationships]);

  const primaryActionByItem = useMemo(() => {
    const map = new Map<string, ItemAction>();
    for (const a of actions) {
      if (a.is_primary && !map.has(a.item_id)) {
        map.set(a.item_id, a);
      }
    }
    return map;
  }, [actions]);

  if (topLevelItems.length === 0) {
    return (
      <p className="mt-10 text-center text-text-light">
        {t("ServicesPage.no-services")}
      </p>
    );
  }

  return (
    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {topLevelItems.map(item => (
        <ServiceCategoryCard
          key={item.id}
          item={item}
          childCount={childCountByParent.get(item.id) ?? 0}
          primaryAction={primaryActionByItem.get(item.id) ?? null}
        />
      ))}
    </div>
  );
};
